import {
  CategoryScale,
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  LogarithmicScale,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chartTrendline from 'chartjs-plugin-trendline';
import {isPlainObject} from 'lodash';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useRef, useState} from 'react';

import ChartConstants, {createXAxisConfig, createYAxisConfig} from 'components/Chart/Constants';


const propTypes = {
  title: PropTypes.string,
  data: PropTypes.oneOfType([
    // simple line chart
    PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })),
    // standard chart.js data
    PropTypes.shape({
      labels: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])),
      datasets: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        data: PropTypes.arrayOf(PropTypes.number).isRequired,
        borderColor: PropTypes.string,
      })).isRequired,
    }),
  ]),
  showTrendLine: PropTypes.bool,
  showLabels: PropTypes.bool,
  /**
   * Custom chart.js `datalabels` config props
   */
  labelConfig: PropTypes.object,
  xAxisLabel: PropTypes.shape({
    maxLength: PropTypes.number,
    stepSize: PropTypes.number,
    display: PropTypes.bool,
    type: PropTypes.string,
    displayTimeFormat: PropTypes.string,
    title: PropTypes.string,
  }),
  yAxisLabel: PropTypes.shape({
    truncate: PropTypes.string,
    maxLength: PropTypes.number,
    stepSize: PropTypes.number,
    display: PropTypes.bool,
    align: PropTypes.string,
    type: PropTypes.string,
    displayTimeFormat: PropTypes.string,
    title: PropTypes.string,
  }),
  className: PropTypes.string,
  style: PropTypes.object,
};
/**
 * @typedef {object} yAxisLabel
 * @property {string} [truncate] - either 'middle' or 'end'
 * @property {number} [maxLength] - maximum length to truncate to (skip truncate if 0)
 * @property {number} [stepSize] - step size is count between ticks. If not set, tick step size is automatically determined by chart.js
 * @property {object} [type] - define the scale type
 * @property {string} [displayTimeFormat] - define the time format
 * @property {string} [title] - axis title
 * @property {boolean} [display] - whether y-axis labels should be displayed
 * @property {string} [align] - default alignment is right but 'left', or 'center' are other options
 *
 * @typedef {object} xAxisLabel
 * @property {number} [maxLength] - wrap label at x-axis tick marks at specified length (no wrap if 0)
 * @property {number} [stepSize] - step size is count between ticks. If not set, tick step size is automatically determined by chart.js
 * @property {boolean} [display] - - whether x-axis labels should be displayed
 * @property {string} [type] - define the scale type (use ChartConstants.scaleTypes)
 * @property {string} [displayTimeFormat] - define the time format
 * @property {string} [title] - axis title
 */
/**
 *
 * Renders line chart.
 *
 * @param {object} props - props container
 * @param {string} [props.title] - assign it to dataset's label
 * @param {Array} props.data - use a simple array of {label, value} or standard chart.js data structure
 * @param {boolean} props.showTrendLine - shows trend line in line chart
 * @param {boolean} props.showLabels - shows labels in line chart
 * @param {object} props.labelConfig - new data label config to change data labels
 * @param {xAxisLabel} [props.xAxisLabel] - config object for x-axis labels
 * @param {yAxisLabel} [props.yAxisLabel] - config object for y-axis labels
 * @param {string} [props.className]
 * @param {object} [props.style]
 * @return {JSX.Element}
 */
export default function LineChart({title, data,
  showTrendLine = false, showLabels = false,
  labelConfig = ChartConstants.defaultLabelConfig,
  xAxisLabel = {
    maxLength: 10,
    stepSize: undefined,
    display: true,
    type: ChartConstants.scaleTypes.LINEAR,
    displayTimeFormat: '',
    title: '',
  },
  yAxisLabel = {
    truncate: ChartConstants.truncateOptions.MIDDLE,
    maxLength: 20,
    stepSize: undefined,
    display: true,
    align: 'right',
    type: ChartConstants.scaleTypes.LINEAR,
    displayTimeFormat: '',
    title: '',
  },
  className, style}) {
  const ref = useRef(null);
  // reference: https://medium.com/@teh_builder/ref-objects-inside-useeffect-hooks-eb7c15198780
  const refCallback = useCallback((node) => {
    ref.current = node;
  }, []);

  const [chart, setChart] = useState(null);
  
  // must set position: relative to get responsive behaviour
  const finalStyle = {
    position: 'relative',
    ...style,
  };

  const componentIsMounted = useRef(true);
  // eslint-disable-next-line
  useEffect(() => {
    return () => {
      componentIsMounted.current = false;
    };
  }, []);
  
  useEffect(() => {
    if (!data || data.length === 0) {
      return;
    }
    if (!ref.current) {
      return;
    }
    
    let labels;
    let datasets;
    if (isPlainObject(data)) {
      labels = data.labels;
      datasets = data.datasets;
    } else {
      labels = data.map((d) => d.label);
      const values = data.map((d) => d.value);
      const borderColor = ChartConstants.colors[0];
      datasets = [{
        data: values,
        borderColor,
      }];
      if (title) {
        datasets[0].label = title;
      }
    }

    if (showTrendLine) {
      datasets = datasets.map((dataset) => {
        if (!dataset.trendlineLinear) {
          return {...dataset, trendlineLinear: ChartConstants.defaultTrendLineConfig};
        }
        return dataset;
      });
    }

    if (!chart) {
      if (chart) {
        chart.destroy();
      }
      const loadChart = async () => {
        Chart.register(CategoryScale, Filler, LinearScale, LineController,
          LineElement, LogarithmicScale, PointElement, TimeScale, Title, Tooltip);
        setChart(new Chart(ref.current.getContext('2d'), {
          type: 'line',
          data: {
            labels,
            datasets,
          },
          ...((showTrendLine || showLabels)
            ? {
              plugins: [
                showTrendLine ? chartTrendline : {},
                showLabels ? ChartDataLabels : {},
              ],
            }
            : {}),
          options: {
            responsive: true,
            maintainAspectRatio: false,
            ...(showLabels ? ChartConstants.defaultLayoutConfig : {}),
            plugins: {
              title: {
                display: true,
                text: title,
              },
              legend: {
                display: false,
              },
              tooltip: {
                enabled: !showLabels,
              },
              ...labelConfig,
            },
            scales: {
              x: createXAxisConfig(xAxisLabel),
              y: createYAxisConfig(yAxisLabel),
            },
          },
        }));
      };
      // noinspection JSIgnoredPromiseFromCall
      loadChart();
    } else {
      chart.data.labels = labels;
      chart.data.datasets = datasets;
      chart.update();
    }

    return () => {
      if (chart && !componentIsMounted.current) {
        chart.destroy();
      }
    };
  }, [title, data, showTrendLine, showLabels]);
  
  return (
    <div className={className} style={finalStyle}>
      <canvas ref={refCallback} aria-label={title || 'Line Chart'} role="img" />
    </div>
  );
}
LineChart.propTypes = propTypes;
